/**
 * AdminFoodsScreen — biblioteca de alimentos para el admin (Nilabs).
 *
 * Mismo patrón que `AdminExercisesScreen`: galería paginada de cards
 * (no tabla — el coach está más cómodo escaneando tarjetas que filas
 * densas), filtros con conteo, click en card → modal grande de edición.
 *
 * La tabla `food_library` es COMPARTIDA con AetherCoach, así que lo que
 * se agrega acá queda disponible para todos los entrenadores y para
 * todos los productos.
 *
 * Diseño de cada card:
 *   ┌────────────────────────────────┐
 *   │ ⬤  CAT          195 kcal       │
 *   │ Pollo a la plancha             │
 *   │ ─────                          │
 *   │ 30 P · 0 C · 8 G               │
 *   │ Proteínas · 100 g · USDA       │
 *   └────────────────────────────────┘
 *
 * El círculo de color codifica la categoría — un proxy visual barato que
 * permite reconocer "esto es Proteínas / Carbos / etc." de un vistazo
 * cuando se está revisando el catálogo a escala.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  adminCreateFood,
  adminDeleteFood,
  adminListFoods,
  adminUpdateFood,
  type AdminFood,
  type AdminFoodInput,
} from "../../api/admin";
import { invalidateFoodCatalog } from "../../api/foods";
import { Icon } from "../../lib/Icon";
import { EmptyState } from "../../lib/components";
import { useToast } from "../../lib/toast";
import { AdminBadge, AdminGate } from "./_shared";

const PAGE_SIZE = 30;

const CATEGORIES = [
  "Proteínas",
  "Carbohidratos",
  "Lácteos",
  "Legumbres",
  "Verduras",
  "Frutas",
  "Grasas y frutos secos",
  "Bebidas",
  "Suplementos",
  "Snacks y otros",
];

const COMMON_UNITS = ["g", "ml", "unidad", "rebanada", "scoop", "taza", "cucharada"];

/** Color + iniciales por categoría — proxy visual mientras no haya foto. */
const CAT_STYLE: Record<string, { color: string; bg: string; ini: string }> = {
  "Proteínas":             { color: "#b91c1c", bg: "#fee2e2", ini: "Pr" },
  "Carbohidratos":         { color: "#a16207", bg: "#fef3c7", ini: "Ca" },
  "Lácteos":               { color: "#1e40af", bg: "#dbeafe", ini: "Lá" },
  "Legumbres":             { color: "#854d0e", bg: "#fef9c3", ini: "Le" },
  "Verduras":              { color: "#166534", bg: "#dcfce7", ini: "Ve" },
  "Frutas":                { color: "#9d174d", bg: "#fce7f3", ini: "Fr" },
  "Grasas y frutos secos": { color: "#92400e", bg: "#ffedd5", ini: "Gr" },
  "Bebidas":               { color: "#0c4a6e", bg: "#e0f2fe", ini: "Be" },
  "Suplementos":           { color: "#581c87", bg: "#f3e8ff", ini: "Su" },
  "Snacks y otros":        { color: "#374151", bg: "#e5e7eb", ini: "Sn" },
};
const CAT_DEFAULT = { color: "#374151", bg: "#e5e7eb", ini: "?" };

export function AdminFoodsScreen() {
  return (
    <AdminGate>
      <AdminFoodsContent />
    </AdminGate>
  );
}

function AdminFoodsContent() {
  const toast = useToast();
  const [foods, setFoods] = useState<AdminFood[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [category, setCategory] = useState<string>("");
  const [page, setPage] = useState(1);

  /** `null` = no hay modal. `"new"` = crear. Objeto = editar. */
  const [editing, setEditing] = useState<AdminFood | "new" | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQuery(query), 250);
    return () => window.clearTimeout(t);
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await adminListFoods({
        query: debouncedQuery || undefined,
        category: category || undefined,
      });
      setFoods(list);
      setPage(1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, category]);

  useEffect(() => {
    load();
  }, [load]);

  const paged = useMemo(() => {
    if (!foods) return [] as AdminFood[];
    const start = (page - 1) * PAGE_SIZE;
    return foods.slice(start, start + PAGE_SIZE);
  }, [foods, page]);

  const totalPages = foods ? Math.max(1, Math.ceil(foods.length / PAGE_SIZE)) : 1;

  /** Conteo por categoría — útil para mostrar al lado de cada filtro. */
  const countByCategory = useMemo(() => {
    const m = new Map<string, number>();
    (foods || []).forEach((f) => {
      const c = f.category || "Sin categoría";
      m.set(c, (m.get(c) || 0) + 1);
    });
    return m;
  }, [foods]);

  const onSave = async (payload: AdminFoodInput, id?: number) => {
    try {
      if (id == null) {
        await adminCreateFood(payload);
        toast(`Alimento "${payload.name}" agregado al catálogo`);
      } else {
        await adminUpdateFood(id, payload);
        toast(`"${payload.name}" actualizado`);
      }
      invalidateFoodCatalog();
      setEditing(null);
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast(`No pude guardar: ${msg}`);
    }
  };

  const onDelete = async (food: AdminFood) => {
    if (!confirm(`¿Eliminar "${food.name}" del catálogo global? Lo dejará de ver todo el equipo (AetherLite + AetherCoach).`)) return;
    try {
      await adminDeleteFood(food.id);
      invalidateFoodCatalog();
      toast(`"${food.name}" eliminado`);
      await load();
    } catch (e) {
      toast("No pude eliminar: " + (e instanceof Error ? e.message : ""));
    }
  };

  return (
    <div className="page al-admin">
      <header className="al-admin-header">
        <div>
          <h1>
            Biblioteca de alimentos <AdminBadge />
          </h1>
          <p className="muted">
            Catálogo global (compartido con AetherCoach). Agregá los alimentos que
            vayas encontrando — los cambios se ven al instante en el selector de
            todos los entrenadores.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setEditing("new")}>
          <Icon name="plus" size={14} /> Nuevo alimento
        </button>
      </header>

      <div className="al-admin-foods-toolbar">
        <div className="al-admin-foods-search">
          <Icon name="search" size={14} />
          <input
            type="text"
            placeholder="Buscar por nombre — pollo, avena, whey…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button className="link-btn" onClick={() => setQuery("")}>
              limpiar
            </button>
          )}
        </div>
        <select
          className="al-admin-foods-cat"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="">Todas las categorías</option>
          {CATEGORIES.map((c) => {
            const n = countByCategory.get(c);
            return (
              <option key={c} value={c}>
                {c}{n ? ` · ${n}` : ""}
              </option>
            );
          })}
        </select>
        <div className="tiny muted al-admin-foods-count">
          {foods ? `${foods.length} alimentos${category ? ` en ${category}` : ""}` : "—"}
        </div>
      </div>

      {error && (
        <EmptyState
          icon="x"
          title="Error al cargar el catálogo"
          body={error}
          action={
            <button className="btn btn-secondary" onClick={load}>
              Reintentar
            </button>
          }
        />
      )}

      {!error && loading && !foods && (
        <div className="muted" style={{ padding: 24 }}>
          Cargando catálogo…
        </div>
      )}

      {!error && foods && foods.length === 0 && (
        <EmptyState
          icon="gift"
          title="Sin resultados"
          body={
            debouncedQuery || category
              ? "Probá con otra búsqueda o categoría."
              : "El catálogo está vacío. Agregá el primer alimento."
          }
          action={
            <button className="btn btn-primary" onClick={() => setEditing("new")}>
              <Icon name="plus" size={14} /> Crear alimento
            </button>
          }
        />
      )}

      {!error && foods && foods.length > 0 && (
        <>
          <div className="al-admin-food-grid">
            {paged.map((f) => (
              <FoodCard key={f.id} food={f} onEdit={() => setEditing(f)} />
            ))}
          </div>

          {totalPages > 1 && (
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          )}
        </>
      )}

      {editing && (
        <FoodForm
          food={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={onSave}
          onDelete={editing !== "new" ? () => onDelete(editing as AdminFood) : undefined}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// FoodCard — card del grid con nombre + macros prominentes + categoría
// ----------------------------------------------------------------------

function FoodCard({ food, onEdit }: { food: AdminFood; onEdit: () => void }) {
  const cat = food.category ? CAT_STYLE[food.category] || CAT_DEFAULT : CAT_DEFAULT;
  return (
    <button type="button" className="al-admin-food-card" onClick={onEdit} title="Editar">
      <div className="al-admin-food-card-hd">
        <span
          className="al-admin-food-cat-bubble"
          style={{ background: cat.bg, color: cat.color }}
          title={food.category || "Sin categoría"}
        >
          {cat.ini}
        </span>
        <div className="al-admin-food-card-kcal">
          <b>{Math.round(food.calories)}</b>
          <span>kcal</span>
        </div>
      </div>
      <div className="al-admin-food-card-name">{food.name}</div>
      <div className="al-admin-food-card-macros">
        <span><b>{food.protein}</b> P</span>
        <span><b>{food.carbs}</b> C</span>
        <span><b>{food.fat}</b> G</span>
      </div>
      <div className="al-admin-food-card-foot">
        <span className="al-admin-food-card-cat">{food.category || "Sin categoría"}</span>
        <span className="al-admin-food-card-portion">
          {food.base_quantity} {food.base_unit}
        </span>
      </div>
    </button>
  );
}

// ----------------------------------------------------------------------
// Pagination — controles de página (idéntico al de admin de ejercicios)
// ----------------------------------------------------------------------

function Pagination({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (p: number) => void }) {
  const items: Array<number | "ellipsis"> = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) items.push(i);
  } else {
    items.push(1);
    if (page > 3) items.push("ellipsis");
    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);
    for (let i = start; i <= end; i++) items.push(i);
    if (page < totalPages - 2) items.push("ellipsis");
    items.push(totalPages);
  }
  return (
    <div className="al-admin-ex-pagination">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className="al-admin-ex-page"
      >
        <Icon name="arrowL" size={12} /> Anterior
      </button>
      {items.map((it, i) =>
        it === "ellipsis" ? (
          <span key={`e${i}`} className="al-admin-ex-page-ellipsis">…</span>
        ) : (
          <button
            key={it}
            type="button"
            className={"al-admin-ex-page" + (it === page ? " is-active" : "")}
            onClick={() => onChange(it)}
          >
            {it}
          </button>
        ),
      )}
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
        className="al-admin-ex-page"
      >
        Siguiente <Icon name="arrow" size={12} />
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------
// FoodForm — modal para crear o editar un alimento (con scroll interno
// y backdrop que ENVUELVE al modal: mismo bug-fix que el de ejercicios)
// ----------------------------------------------------------------------

interface FoodFormProps {
  food: AdminFood | null; // null = crear
  onClose: () => void;
  onSave: (payload: AdminFoodInput, id?: number) => Promise<void>;
  onDelete?: () => void | Promise<void>;
}

function FoodForm({ food, onClose, onSave, onDelete }: FoodFormProps) {
  const [name, setName] = useState(food?.name || "");
  const [category, setCategory] = useState(food?.category || "");
  const [baseQty, setBaseQty] = useState<string>(String(food?.base_quantity ?? 100));
  const [baseUnit, setBaseUnit] = useState(food?.base_unit || "g");
  const [calories, setCalories] = useState<string>(String(food?.calories ?? ""));
  const [protein, setProtein] = useState<string>(String(food?.protein ?? ""));
  const [carbs, setCarbs] = useState<string>(String(food?.carbs ?? ""));
  const [fat, setFat] = useState<string>(String(food?.fat ?? ""));
  const [dataSource, setDataSource] = useState(food?.data_source || "");
  const [saving, setSaving] = useState(false);

  const canSave =
    name.trim().length > 0 &&
    calories !== "" &&
    protein !== "" &&
    carbs !== "" &&
    fat !== "";

  const submit = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      await onSave(
        {
          name: name.trim(),
          base_unit: baseUnit.trim() || "g",
          base_quantity: Number(baseQty) || 100,
          calories: Number(calories) || 0,
          protein: Number(protein) || 0,
          carbs: Number(carbs) || 0,
          fat: Number(fat) || 0,
          category: category.trim() || null,
          data_source: dataSource.trim() || null,
        },
        food?.id,
      );
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Vista previa del color de la categoría que el coach está eligiendo
  const catPreview = category ? CAT_STYLE[category] || CAT_DEFAULT : CAT_DEFAULT;

  return (
    // Backdrop ENVUELVE al modal (no hermano). Patrón corregido — ver el
    // mismo bug-fix en AdminExercisesScreen: si son hermanos el modal no
    // se centra y los clicks lo cierran.
    <div className="al-modal-backdrop" onClick={onClose}>
      <div
        className="al-modal al-admin-food-form"
        role="dialog"
        aria-label="Alimento"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="al-modal-hd">
          <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>
            {food ? `Editar "${food.name}"` : "Nuevo alimento"}
          </h3>
          <button className="icon-btn" onClick={onClose} title="Cerrar">
            <Icon name="x" size={16} />
          </button>
        </div>

        <div className="al-admin-food-form-body">
          {/* Columna izquierda: bubble grande de categoría + preview de macros
              en vivo (kcal + P/C/G) — el coach ve cómo va quedando la card. */}
          <aside className="al-admin-food-form-aside">
            <div
              className="al-admin-food-form-bubble"
              style={{ background: catPreview.bg, color: catPreview.color }}
              title={category || "Sin categoría"}
            >
              {catPreview.ini}
            </div>
            <div className="al-admin-food-form-preview">
              <div className="al-admin-food-form-prev-name">
                {name.trim() || "Sin nombre"}
              </div>
              <div className="al-admin-food-form-prev-kcal">
                <b>{Math.round(Number(calories) || 0)}</b> kcal
              </div>
              <div className="al-admin-food-form-prev-macros">
                <span><b>{protein || 0}</b> P</span>
                <span><b>{carbs || 0}</b> C</span>
                <span><b>{fat || 0}</b> G</span>
              </div>
              <div className="tiny muted" style={{ marginTop: 6 }}>
                Por {baseQty || "100"} {baseUnit || "g"}
              </div>
            </div>
          </aside>

          {/* Columna derecha: campos editables */}
          <div className="al-admin-food-form-fields">
            <label className="al-food-custom-field">
              <span className="tiny" style={{ fontWeight: 600 }}>Nombre *</span>
              <input
                type="text"
                className="al-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej. Quinua roja cocida"
                autoFocus
              />
            </label>

            <div className="al-admin-food-form-grid2">
              <label className="al-food-custom-field">
                <span className="tiny" style={{ fontWeight: 600 }}>Categoría</span>
                <input
                  type="text"
                  className="al-input"
                  list="al-admin-food-cat-list"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="Carbohidratos, Proteínas…"
                />
                <datalist id="al-admin-food-cat-list">
                  {CATEGORIES.map((c) => <option key={c} value={c} />)}
                </datalist>
              </label>
              <label className="al-food-custom-field">
                <span className="tiny" style={{ fontWeight: 600 }}>Porción base</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <input
                    type="number"
                    className="al-input"
                    value={baseQty}
                    onChange={(e) => setBaseQty(e.target.value)}
                    style={{ width: 90 }}
                    placeholder="100"
                  />
                  <input
                    type="text"
                    className="al-input"
                    list="al-admin-food-unit-list"
                    value={baseUnit}
                    onChange={(e) => setBaseUnit(e.target.value)}
                    placeholder="g"
                  />
                  <datalist id="al-admin-food-unit-list">
                    {COMMON_UNITS.map((u) => <option key={u} value={u} />)}
                  </datalist>
                </div>
              </label>
            </div>

            <div className="al-admin-food-form-section-label">
              MACROS POR {baseQty || "100"} {baseUnit || "g"}
            </div>
            <div className="al-admin-food-form-grid4">
              <label className="al-food-custom-field">
                <span className="tiny" style={{ fontWeight: 600 }}>Kcal *</span>
                <input
                  type="number"
                  className="al-input"
                  value={calories}
                  onChange={(e) => setCalories(e.target.value)}
                />
              </label>
              <label className="al-food-custom-field">
                <span className="tiny" style={{ fontWeight: 600 }}>Proteína (g) *</span>
                <input
                  type="number"
                  step="0.1"
                  className="al-input"
                  value={protein}
                  onChange={(e) => setProtein(e.target.value)}
                />
              </label>
              <label className="al-food-custom-field">
                <span className="tiny" style={{ fontWeight: 600 }}>Carbos (g) *</span>
                <input
                  type="number"
                  step="0.1"
                  className="al-input"
                  value={carbs}
                  onChange={(e) => setCarbs(e.target.value)}
                />
              </label>
              <label className="al-food-custom-field">
                <span className="tiny" style={{ fontWeight: 600 }}>Grasas (g) *</span>
                <input
                  type="number"
                  step="0.1"
                  className="al-input"
                  value={fat}
                  onChange={(e) => setFat(e.target.value)}
                />
              </label>
            </div>

            <label className="al-food-custom-field">
              <span className="tiny" style={{ fontWeight: 600 }}>
                Fuente del dato <span className="muted">(opcional — USDA, etiqueta, etc.)</span>
              </span>
              <input
                type="text"
                className="al-input"
                value={dataSource}
                onChange={(e) => setDataSource(e.target.value)}
                placeholder="USDA · Megaplex · Etiqueta producto…"
              />
            </label>

            <div className="al-admin-food-form-actions">
              {onDelete && (
                <button
                  type="button"
                  className="btn btn-ghost al-admin-food-form-del"
                  onClick={onDelete}
                  disabled={saving}
                >
                  <Icon name="x" size={12} /> Eliminar
                </button>
              )}
              <div style={{ flex: 1 }} />
              <button className="btn btn-ghost" onClick={onClose} type="button" disabled={saving}>
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={submit}
                type="button"
                disabled={!canSave || saving}
              >
                {saving ? "Guardando…" : food ? "Guardar cambios" : "Crear alimento"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
